/**
 * Auth + apiFetch — SPEC-20260505-1712 (BFF cookie session).
 *
 * Migrado de sessionStorage JWT (mock) → cookie HttpOnly via @wynk/auth-bff.
 * Frontend não vê nem armazena tokens — backend valida cookie, retorna user
 * via `/api/auth/me`, e injeta cookie automaticamente em chamadas
 * same-origin (`credentials: "include"`).
 *
 * Login flow: redirect pra `/api/auth/oidc/login?returnTo=<path>`.
 * Logout: POST `/api/auth/logout` + redirect pra Keycloak end-session.
 */

import { config } from "../config";
import type { AuthUser, WosRole } from "../types/auth";

interface MeResponse {
  jti: string;
  kcSub: string;
  appSlug: string;
  features: string[];
  expiresAt: string;
  createdAt: string;
  preferredUsername?: string;
  email?: string;
  realmRoles?: string[];
}

/**
 * Busca dados da sessão atual via `GET /api/auth/me`. Cookie viaja
 * automático via `credentials: "include"`. Retorna null se 401 (sessão
 * ausente/expirada).
 *
 * Bugfix 2026-05-06: a lib `@wynk/auth-bff` retorna `SessionPublicView`
 * **direto** (sem o envelope `{success, data}` do WOS API). Antes desse
 * fix, o parser tratava o response como envelope, `body.success` era
 * undefined, fetchCurrentUser retornava null, e RequireAuth fazia loop
 * infinito de redirect /login ↔ / (descoberto em smoke manual após SPEC-2250).
 */
export async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    const url = `${config.apiUrl}/api/auth/me`;
    const res = await fetch(url, { credentials: "include" });
    if (res.status === 401) return null;
    if (!res.ok) {
      console.warn(`[auth] /api/auth/me retornou ${res.status}`);
      return null;
    }
    // Lib `@wynk/auth-bff` retorna SessionPublicView raw (não envelope WOS).
    const me = (await res.json()) as MeResponse;
    if (!me.kcSub) return null;
    return {
      sub: me.kcSub,
      name: me.preferredUsername ?? me.email,
      email: me.email,
      roles: (me.realmRoles ?? []) as WosRole[],
      features: me.features ?? [],
    };
  } catch (err) {
    console.warn("[auth] fetchCurrentUser falhou", err);
    return null;
  }
}

/**
 * Inicia login OIDC. Redireciona o browser para o backend, que redireciona
 * pro Keycloak. Após callback bem-sucedido, backend volta pro `returnTo`
 * com cookie de sessão setado.
 */
export function startLogin(returnTo: string = "/"): void {
  const url = `${config.apiUrl}/api/auth/oidc/login?returnTo=${encodeURIComponent(returnTo)}`;
  window.location.assign(url);
}

/**
 * Logout — POST /api/auth/logout invalida session no Redis + limpa cookie.
 * Backend devolve redirect pro Keycloak end-session, que finaliza SLO e
 * volta pra `/login`.
 */
export async function logout(): Promise<void> {
  const url = `${config.apiUrl}/api/auth/logout`;
  const res = await fetch(url, { method: "POST", credentials: "include" });
  if (res.redirected) {
    window.location.assign(res.url);
  } else if (res.ok) {
    window.location.assign("/login");
  } else {
    // Mesmo em erro, força logout client-side
    window.location.assign("/login");
  }
}

const inflightGets = new Map<string, Promise<unknown>>();

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const url = `${config.apiUrl}${path.startsWith("/") ? "" : "/"}${path}`;

  if (method === "GET" && inflightGets.has(url)) {
    return inflightGets.get(url) as Promise<T>;
  }

  const headers = new Headers(options.headers);
  if (!headers.has("Content-Type") && options.body !== undefined && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const promise = (async (): Promise<T> => {
    const res = await fetch(url, { ...options, headers, credentials: "include" });
    if (res.status === 401) {
      // Sessão ausente/expirada — redireciona pra login. Cookie já foi cleared
      // pelo backend na resposta (se aplicável); se não, próxima chamada
      // também vai 401 e re-redireciona.
      window.location.assign("/login");
      throw new Error("Sessão expirada. Redirecionando…");
    }
    const text = await res.text();
    let body: unknown = null;
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        body = text;
      }
    }
    if (!res.ok) {
      const msg =
        (typeof body === "object" && body !== null && "error" in body && typeof (body as { error: unknown }).error === "object"
          ? ((body as { error: { message?: string } }).error.message ?? `HTTP ${res.status}`)
          : `HTTP ${res.status}`);
      throw new Error(msg);
    }
    return body as T;
  })();

  if (method === "GET") {
    inflightGets.set(url, promise);
    promise.finally(() => inflightGets.delete(url));
  }

  return promise;
}
